<script>
  // Thin, canonical DaisyUI button wrapper. Every button in the admin app should
  // go through this so sizing, icon spacing, and loading state stay identical.
  //
  //   <Button variant="primary" icon="add" onclick={...}>New quest</Button>
  //   <Button variant="ghost" size="sm" square icon="edit" aria-label="Edit" />
  //   <Button variant="error" outline loading={$form.processing}>Delete</Button>
  //   <Button variant="primary" href="/quests/new" icon="add">New quest</Button>   <!-- Inertia SPA nav -->
  //   <Button href={stripeUrl} external target="_blank" icon="external">Stripe</Button> <!-- plain <a> -->
  //
  // `variant` picks the DaisyUI color; `outline`/`soft` are style modifiers.
  // `href` renders an Inertia <Link> (internal SPA nav) unless `external` (or a
  // `target`) is set, in which case it renders a plain <a>. `class` is merged
  // LAST so callers can always override.
  import { Link } from '@inertiajs/svelte'
  import Icon from './icon.svelte'

  let {
    variant = 'primary',
    size = 'md',
    icon = null,
    iconEnd = null,
    outline = false,
    soft = false,
    block = false,
    circle = false,
    square = false,
    loading = false,
    disabled = false,
    type = 'button',
    href = null,
    external = false,
    class: className = '',
    children,
    ...rest
  } = $props()

  const VARIANTS = {
    default: '', // plain DaisyUI .btn (neutral gray) — no color modifier
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    accent: 'btn-accent',
    neutral: 'btn-neutral',
    ghost: 'btn-ghost',
    link: 'btn-link',
    info: 'btn-info',
    success: 'btn-success',
    warning: 'btn-warning',
    error: 'btn-error'
  }

  const SIZES = { xs: 'btn-xs', sm: 'btn-sm', md: '', lg: 'btn-lg' }
  const ICON_SIZE = { xs: 'xs', sm: 'sm', md: 'sm', lg: 'md' }

  const classes = $derived(
    [
      'btn',
      VARIANTS[variant] ?? VARIANTS.primary,
      SIZES[size] ?? '',
      outline ? 'btn-outline' : '',
      soft ? 'btn-soft' : '',
      block ? 'btn-block' : '',
      circle ? 'btn-circle' : '',
      square ? 'btn-square' : '',
      disabled ? 'btn-disabled' : '',
      className
    ]
      .filter(Boolean)
      .join(' ')
  )

  const isExternal = $derived(external || !!rest.target)
</script>

{#snippet inner()}
  {#if loading}
    <span class="loading loading-spinner loading-xs"></span>
  {:else if icon}
    <Icon name={icon} size={ICON_SIZE[size] ?? 'sm'} />
  {/if}
  {@render children?.()}
  {#if iconEnd && !loading}
    <Icon name={iconEnd} size={ICON_SIZE[size] ?? 'sm'} />
  {/if}
{/snippet}

{#if href && isExternal}
  <a {href} class={classes} {...rest}>{@render inner()}</a>
{:else if href}
  <Link {href} class={classes} {...rest}>{@render inner()}</Link>
{:else}
  <button {type} class={classes} disabled={disabled || loading} {...rest}>{@render inner()}</button>
{/if}
